import * as React from 'react'
import { HTMLProps, forwardRef } from 'react'
import { FixedSizeList, areEqual } from 'react-window'

import { Box, Table } from '@factorialco/deprecated-design-system'
import { useResizeObserver } from 'usehooks-ts'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { type LegalEntity } from 'modules/employees/hooks/useLegalEntities'
import type { TableSelection } from 'modules/employees/pages/EditInBulk/hooks/useTableSelection'
import {
  CustomField,
  EmployeeCustomFieldsValue,
  EmployeeGroups,
  HEADER_HEIGHT,
  ROW_HEIGHT,
} from 'modules/employees/pages/EditInBulk/types'
import type { ColumnNames } from 'modules/employees/pages/EditInBulk/vars'
import useResizeVirtualizeTable from 'modules/employees/pages/ImportProcessPage/components/ImportProcessWizard/EditionStep/components/useResizeVirtualizedTable'
import type Employee from 'modules/employees/store/models/Employee'

import Header from './Header'
import type { ColumnsType } from './Header'
import EmployeeRow from './Rows/Employee'

export type DataTableColumnsType = ColumnsType

interface DataTableProps {
  employees: Employee[]
  legalEntities: LegalEntity[]
  columns: DataTableColumnsType
  tableSelection: TableSelection
  onSelect: (columnName: ColumnNames, selectedId: number) => void
  onAllSelect: (column: ColumnNames, selectedIds: number[]) => void
  employeeGroups: EmployeeGroups[]
  customFields: CustomField[]
  customFieldsValues: EmployeeCustomFieldsValue[]
}

const DataTable = ({
  columns,
  employees,
  legalEntities,
  tableSelection,
  onSelect,
  onAllSelect,
  employeeGroups,
  customFields,
  customFieldsValues,
}: DataTableProps) => {
  const tableHeadRef = React.useRef<HTMLTableSectionElement>(null)
  const tableBodyRef = React.useRef<HTMLTableSectionElement>(null)
  const tableContainerRef = React.useRef<HTMLDivElement>(null)

  const { tableBodyRect } = useResizeVirtualizeTable({
    tableHeadRef,
    tableBodyRef,
    tableContainerRef,
    loading: false,
  })

  const legalEntityColSpan = useUnsafeMemo(
    () => (legalEntities.length > 1 && employeeGroups.length > 1 ? 2 : 1),
    [legalEntities, employeeGroups]
  )

  const { height: tableHeight } = useResizeObserver({ ref: tableContainerRef })

  const TableWrapper = forwardRef<HTMLDivElement, HTMLProps<HTMLDivElement>>(function TableWrapper({
    children,
  }) {
    return (
      <Table grid noRadius overflowY="auto">
        <Header
          columns={columns}
          employees={employees}
          tableSelection={tableSelection}
          onAllSelect={onAllSelect}
          legalEntityColSpan={legalEntityColSpan}
          tableHeadRef={tableHeadRef}
        />
        <Box style={{ top: `${HEADER_HEIGHT}px` }} position="absolute">
          <Table.Body ref={tableBodyRef}>{children}</Table.Body>
        </Box>
      </Table>
    )
  })

  const RowsWrapper = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const employee = employees[index]

    if (!employee) return null

    return (
      <EmployeeRow
        key={employee.id}
        employee={employee}
        legalEntities={legalEntities}
        tableSelection={tableSelection}
        onSelect={onSelect}
        employeeGroups={employeeGroups}
        customFields={customFields}
        customFieldsValues={customFieldsValues}
        legalEntityColSpan={legalEntityColSpan}
        style={style}
      />
    )
  }

  const RowsWrapperMemo = React.memo(RowsWrapper, areEqual)

  return (
    <Box
      width="full"
      height="full"
      overflowY="scroll"
      overflowX="scroll"
      flexDirection="column"
      ref={tableContainerRef}
    >
      <FixedSizeList
        width={tableBodyRect.width}
        height={tableHeight ?? 0}
        itemCount={employees.length}
        itemSize={ROW_HEIGHT}
        innerElementType={TableWrapper}
        style={{
          borderSpacing: 0,
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          margin: 0,
          width: 'inherit',
        }}
      >
        {RowsWrapperMemo}
      </FixedSizeList>
    </Box>
  )
}

export default DataTable
